import { styled, useMediaQuery, useTheme } from "@mui/material";
import _get from "lodash/get";
import moment from "moment";
import { RetailerWithSyncStatus } from "../../../../../../redux/types";
import {
  formatMoney,
  getEntityImage,
} from "../../../../../../utils/StringUtils";
import { ButtonLoadingEllipsis } from "../../../../../StoreComponents/StoreButton";
import ConnectRetailerDialog from "../../../../../StoreComponents/StoreDialogs/ConnectRetailerDialog";
import DisconnectRetailerDialog from "../../../../../StoreComponents/StoreDialogs/DisconnectRetailerDialog";
import { useSyncContext } from "../../../../../SyncProvider";

import {
  Button,
  CheckCircleIcon,
  MOBILE_SCREEN_SIZE,
  SMALL_MOBILE_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { syncRetailerServerSide } from "../../../../../../api";
import { useSiteContext } from "../../../../../SiteProvider";
import useStoreType from "../../../../../../utils/hooks/useStoreType";
import { StoreBrandingType } from "../../../../../../types/misc";
import { useNavigate } from "react-router-dom";
import { useSignedIn } from "@/utils/hooks/useSignedIn";

const RetailerCardContainer = styled("div", {
  label: "RetailerCardContainer",
})(({ theme }) => ({
  padding: "27px 15px 21px 15px",
  borderRadius: 10,
  border: `solid 1px ${theme.vars.palette.divider}`,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 17,
  [`@media screen and (max-width: ${1250}px)`]: {},
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: 10,
    padding: "20.9px 15px",

    maxWidth: 260,
  },
  [`@media (max-width: ${SMALL_MOBILE_SCREEN_SIZE}px)`]: {
    gap: 10,
    padding: "20.9px 15px",

    maxWidth: 155,
  },
  ".logo": {
    width: `46%`,
    maxHeight: 80,
    maxWidth: 80,
    aspectRatio: "1/1",
    objectFit: "contain",
  },
  ".name": {
    fontSize: 16,
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    [`@media screen and (max-width: ${1250}px)`]: {},
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 16,
    },
  },
  ".status": {
    fontSize: 14,
    textAlign: "center",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    [`@media screen and (max-width: ${1250}px)`]: {},
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
}));

const RetailerCardButton = styled(Button, {
  label: "RetailerCardButton",
})(({ theme }) => ({
  width: 141.5,
  minWidth: 108,
  font: theme.vars.font.smButton,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    width: "auto",
    font: theme.vars.font.xsButton,
    minWidth: 88,
  },
}));

const RetailerCard = ({
  retailer,
  disconnect,
}: {
  retailer: RetailerWithSyncStatus;
  disconnect?: boolean;
}) => {
  const theme = useTheme();
  const storeType = useStoreType();
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const { retailerConnections } = useSyncContext();
  const { signedIn } = useSignedIn();
  const navigate = useNavigate();
  const { abTestingTreatments } = useSiteContext();
  const mobileView = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  const rewardAmount = retailer.retailerConnectRewardAmount ?? 0;
  let lastScanTime = _get(
    retailerConnections,
    [`${retailer.parentRetailerId || retailer.retailerId}`, "lastSyncTime"],
    null
  );

  const treatment = abTestingTreatments.syncOptionForRetailerTreatment;

  const handleConnect = () => {
    if (isDtc) {
      navigate("/connect", { state: { retailerId: retailer.retailerId } });
    } else {
      ConnectRetailerDialog.show(retailer.retailerId);
    }
  };

  const getStatusText = () => {
    if (retailer.currentSyncStatus === "syncing") {
      return "Syncing";
    }
    if (retailer.connectionStatus === "new" && rewardAmount > 0) {
      return `to earn ${formatMoney(rewardAmount)}`;
    }
    if (retailer.connectionStatus === "invalid") {
      return "Disconnected";
    }

    if (!lastScanTime) {
      return "";
    }

    if (moment(lastScanTime).isAfter(moment().add(-1, "d"))) {
      return `Synced ${moment(lastScanTime).fromNow()}`;
    }

    return `Synced on ${moment(lastScanTime).format("MMM D")}`;
  };

  const statusText = getStatusText();

  return (
    <RetailerCardContainer>
      <img
        alt="logo"
        className="logo"
        src={getEntityImage(retailer.retailerId, "retailer")}
      ></img>
      <div className="name">{retailer.displayName}</div>
      <div className="button">
        {disconnect ? (
          <RetailerCardButton
            height={{ xs: 30, sm: 38.5 }}
            variant="outline"
            color="primary"
            onClick={() => {
              DisconnectRetailerDialog.show(retailer);
            }}
          >
            Disconnect
          </RetailerCardButton>
        ) : (
          <>
            {retailer.connectionStatus === "valid" && (
              <RetailerCardButton
                height={{ xs: 30, sm: 38.5 }}
                variant="outline"
                color="default"
                disabled={retailer.currentSyncStatus !== "none"}
              >
                {retailer.currentSyncStatus === "syncing" ? (
                  <>
                    Syncing
                    <ButtonLoadingEllipsis
                      baseFontSize={mobileView ? 17 : 12}
                      blue
                    />
                  </>
                ) : (
                  <>
                    <CheckCircleIcon
                      backgroundColor={theme.vars.palette.secondary.main}
                      htmlColor={theme.vars.palette.secondary.contrastText}
                      data-theme-color="secondary"
                      sx={{
                        marginRight: "8px",
                        maxWidth: 17,
                        maxHeight: 17,
                        width: "fit-content",
                        height: "fit-content",
                        padding: 0,

                        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                          marginRight: "4px",
                        },
                      }}
                    />
                    <span>Success</span>
                  </>
                )}
              </RetailerCardButton>
            )}
            {retailer.connectionStatus === "invalid" && (
              <RetailerCardButton
                height={{ xs: 30, sm: 38.5 }}
                variant="solid"
                color="primary"
                onClick={() => {
                  handleConnect();
                }}
                disabled={retailer.currentSyncStatus !== "none"}
              >
                {retailer.currentSyncStatus === "syncing" ? (
                  <>
                    Syncing
                    <ButtonLoadingEllipsis
                      baseFontSize={mobileView ? 17 : 12}
                    />
                  </>
                ) : (
                  "Fix"
                )}
              </RetailerCardButton>
            )}
            {retailer.connectionStatus === "new" && (
              <RetailerCardButton
                height={{ xs: 30, sm: 38.5 }}
                variant="solid"
                color="tertiary"
                onClick={() => {
                  if (signedIn) {
                    handleConnect();
                  } else {
                    navigate("/signin");
                  }
                }}
                disabled={retailer.currentSyncStatus !== "none"}
              >
                {retailer.currentSyncStatus === "syncing" ? (
                  <>
                    Syncing
                    <ButtonLoadingEllipsis
                      baseFontSize={mobileView ? 17 : 12}
                      blue
                    />
                  </>
                ) : (
                  "Connect"
                )}
              </RetailerCardButton>
            )}
          </>
        )}
        {treatment === "on" && (
          <RetailerCardButton
            height={{ xs: 30, sm: 38.5 }}
            variant="outline"
            color="primary"
            onClick={() => {
              syncRetailerServerSide({
                req: {
                  parentRetailerIds: [
                    retailer.parentRetailerId || retailer.retailerId,
                  ],
                },
              });
            }}
          >
            Sync
          </RetailerCardButton>
        )}
      </div>
      <div className="status">{statusText}</div>
    </RetailerCardContainer>
  );
};

export default RetailerCard;
